import { useState, useEffect } from "react";
import { Menu, X, Monitor, Leaf, Globe, Users, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

export const Header = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = 300;
      setIsScrolled(window.scrollY > heroHeight);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: "Software e Recursos", path: "/software-recursos", icon: Monitor },
    { label: "Programas Ambientais", path: "/programas-ambientais", icon: Leaf },
    { label: "Serviços Online", path: "/servicos-online", icon: Globe },
    { label: "Nossa Equipe", path: "/equipe", icon: Users },
    { label: "Fale Conosco", path: "/cadastro", icon: MessageCircle },
  ];

  const handleMenuClick = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  const closeMobileMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowMobileMenu(false);
      setIsClosing(false);
    }, 200);
  };

  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      closeMobileMenu();
    } else {
      setShowMobileMenu(true);
    }
  };

  return (
    <header className={`fixed z-50 w-full transition-all duration-300 ${
      isScrolled 
        ? 'bg-gray-900 shadow-lg backdrop-blur-sm' 
        : 'bg-blue-600/95 backdrop-blur-sm'
    }`}>
      <div className="flex flex-row justify-between items-center py-2 sm:py-3 px-4 lg:px-16 xl:px-40">
        <div className="flex flex-row justify-center items-center">
          <button 
            onClick={() => navigate('/')} 
            className="flex flex-row justify-center items-center transition-transform duration-200 hover:scale-105"
          >
            <img 
              src="lovable-uploads/2dbb99b7-ea6c-49df-8d15-b6fcbba9660d.png" 
              className="h-12 w-auto lg:h-16 lg:w-auto object-contain" 
              alt="Logo AHQ" 
            />
          </button>
        </div>

        {/* Desktop Navigation - visible only on large screens */}
        <nav className="hidden lg:flex lg:flex-row items-center gap-4 text-xl">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className="flex items-center gap-1.5 font-medium text-sm px-3 py-1.5 rounded border transition-all duration-200 hover:scale-105 bg-blue-600 text-white border-blue-500 hover:bg-blue-500 hover:border-blue-400"
              >
                <IconComponent className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Mobile menu button - visible only on small screens */}
        <div className="flex lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
            className={`transition-all duration-300 ${
              isScrolled
                ? 'text-white bg-gray-700 border border-gray-600 hover:bg-gray-600'
                : 'text-white bg-blue-700 border border-blue-500 hover:bg-blue-500'
            }`}
          >
            {showMobileMenu && !isClosing ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu sliding below header */}
      {showMobileMenu && (
        <div 
          className={`lg:hidden overflow-hidden shadow-[0_4px_6px_rgba(0,0,0,0.3)] ${
            isClosing ? 'animate-slide-up' : 'animate-slide-down'
          } ${isScrolled ? 'bg-gray-800' : 'bg-blue-700/95 backdrop-blur-sm'}`}
        >
          <nav className="flex flex-col items-center gap-3 py-4 px-4">
            {menuItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Button
                  key={index}
                  className="bg-blue-600 text-white font-semibold border border-blue-700 hover:bg-blue-700 hover:border-blue-400 hover:scale-105 justify-center h-12 w-1/2 max-w-[400px] transition-all duration-200 gap-2"
                  onClick={() => handleMenuClick(item.path)}
                >
                  <IconComponent className="w-4 h-4" />
                  {item.label}
                </Button>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};
